import {
  VentilatedWallFacadeCladdingTypes,
  VentilatedWallInsulationTypes,
  VentilatedWallWallTypeTypes,
} from "@iko-design-center/shared"
import { useHistory } from "react-router"
import { useRoutes } from "../routes/hooks"
import { Models } from "../../components/viewer/renderer.api"
import { useApplication } from "../application/hooks"
import { VentilatedWallData } from "../ventilated-wall/store"
import { useVentilatedWall } from "../ventilated-wall/hooks"
import { checkValidQueriedValueOfType, getEnumKey } from "./query-helpers"
import { useTranslation } from "react-i18next"

export function useQueryParams() {
  const { setCurrentModel } = useApplication()
  const { t } = useTranslation() as any
  const { Routes } = useRoutes()
  const history = useHistory()

  const {
    setVentilatedWallWallType,
    setVentilatedWallFacadeCladdingType,
    setVentilatedWallInsulationType,
    setVentilatedWallWallTypeWithThickness,
    setVentilatedWallInsulationTypeWithThickness,
  } = useVentilatedWall()

  const setQueryParams = (
    data: VentilatedWallData,
    language: string,
    redirect = false
  ) => {
    const languageRoute = t("routes.VENTILATED_WALL_3_OVERVIEW", {
      lng: language,
    })
    const urlWithoutParams = `${window.location.origin}${languageRoute}`
    const url = new URL(urlWithoutParams ?? window.location.href)

    // Wall type
    const wallType = data.wallType.type
    const wallTypeEnum = getEnumKey(wallType, VentilatedWallWallTypeTypes)
    if (wallTypeEnum) {
      url.searchParams.append("wall-type", wallTypeEnum)

      // Include wall type thickness
      const wallTypeThermalThickness =
        data.thermalPerformance.wallType.thickness
      if (wallTypeThermalThickness) {
        url.searchParams.append(
          "wall-type-thickness",
          wallTypeThermalThickness.toString()
        )
      }
    }

    // Facade cladding
    const facadeCladding = data.facadeCladding.type
    const facadeCladdingEnum = getEnumKey(
      facadeCladding,
      VentilatedWallFacadeCladdingTypes
    )
    if (facadeCladdingEnum) {
      url.searchParams.append("facade-cladding", facadeCladdingEnum)
    }

    // Wall insulation
    const wallInsulation = data.wallInsulation.type
    const wallInsulationEnum = getEnumKey(
      wallInsulation,
      VentilatedWallInsulationTypes
    )
    if (wallInsulationEnum) {
      url.searchParams.append("wall-insulation", wallInsulationEnum)

      // Include wall insulation thickness
      const wallInsulationThermalThickness =
        data.thermalPerformance.wallInsulation.thickness
      if (wallInsulationThermalThickness) {
        url.searchParams.append(
          "wall-insulation-thickness",
          wallInsulationThermalThickness.toString()
        )
      }
    }

    if (redirect) {
      window.location.href = url.href
    } else {
      window.history.replaceState([], document.title, url.href as string)
    }
  }

  const redirectBasedOnQueryParams = () => {
    const url = new URL(window.location.href)
    const query = {
      wallType: url.searchParams.get("wall-type"),
      wallTypeThickness: url.searchParams.get("wall-type-thickness"),
      facadeCladding: url.searchParams.get("facade-cladding"),
      wallInsulation: url.searchParams.get("wall-insulation"),
      wallInsulationThickness: url.searchParams.get(
        "wall-insulation-thickness"
      ),
      detail: url.searchParams.get("detail"),
      token: url.searchParams.get("token"),
    }

    // Wall Type
    const validWallType = checkValidQueriedValueOfType(
      query.wallType,
      VentilatedWallWallTypeTypes
    )
    if (validWallType) {
      // Include wall type thickness
      if (query.wallTypeThickness) {
        setVentilatedWallWallTypeWithThickness(
          validWallType,
          query.wallTypeThickness
        )
      } else {
        setVentilatedWallWallType(validWallType)
      }
    }

    // Facade cladding
    const validFacadeCladding = checkValidQueriedValueOfType(
      query.facadeCladding,
      VentilatedWallFacadeCladdingTypes
    )
    if (validFacadeCladding)
      setVentilatedWallFacadeCladdingType(validFacadeCladding)

    // Wall Insulation
    const validWallInsulation = checkValidQueriedValueOfType(
      query.wallInsulation,
      VentilatedWallInsulationTypes
    )
    if (validWallInsulation) {
      // Include wall insulation thickness
      if (query.wallInsulationThickness) {
        setVentilatedWallInsulationTypeWithThickness(
          validWallInsulation,
          query.wallInsulationThickness
        )
      } else {
        setVentilatedWallInsulationType(validWallInsulation)
      }
    }

    // Model
    const validModel = checkValidQueriedValueOfType(query.detail, Models)
    if (validModel) setCurrentModel(validModel)
    else setCurrentModel(Models.VENTILATED_WALL)

    // Routing
    let route = Routes.VENTILATED_WALL_1_WALL_TYPE
    if (validWallType && validFacadeCladding && validWallInsulation) {
      route = Routes.VENTILATED_WALL_3_OVERVIEW
    } else if (validWallType) {
      route = Routes.VENTILATED_WALL_2_FACADE_CLADDING
    }

    if (query.token) {
      history.push({
        pathname: route as any,
        search: `?token=${query.token}`,
      })
    } else {
      history.push(route as any)
    }
  }

  return { setQueryParams, redirectBasedOnQueryParams }
}
